import { CodeText, EmptyState, SectionLabel } from "@/components/common";

import type { SocialPostGenerationTaskDetail } from "../types";

type ContinuityReferenceList = SocialPostGenerationTaskDetail["continuityReferences"];
type ContinuityReference = ContinuityReferenceList[number];

export function getContinuityReferenceRoleLabel(role: ContinuityReference["role"]): string {
  switch (role) {
    case "cast_reference_sheet":
      return "Cast reference sheet";
    case "setting_or_object_reference":
      return "Setting/object reference";
  }
}

export function getContinuityModeLabel(mode: ContinuityReference["visualContinuityMode"]): string {
  switch (mode) {
    case "recurring_people":
      return "Recurring people";
    case "recurring_setting_or_object":
      return "Recurring setting/object";
    case "thematic_only":
      return "Thematic only";
  }
}

const sortContinuityReferences = (references: ContinuityReferenceList): ContinuityReferenceList =>
  references.toSorted(
    (left, right) =>
      left.position - right.position ||
      left.createdAt.localeCompare(right.createdAt) ||
      left.generatedAssetId.localeCompare(right.generatedAssetId),
  );

function ReferenceBadge({ children }: { children: string }) {
  return (
    <span className="rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
      {children}
    </span>
  );
}

export function ContinuityReferenceGrid({ references }: { references: ContinuityReferenceList }) {
  if (references.length === 0) {
    return (
      <EmptyState
        title="No continuity references"
        description="This attempt did not persist carousel continuity reference images."
        className="min-h-40 rounded-2xl bg-muted/20"
      />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {sortContinuityReferences(references).map((reference) => {
        const roleLabel = getContinuityReferenceRoleLabel(reference.role);
        const modeLabel = getContinuityModeLabel(reference.visualContinuityMode);

        return (
          <article
            key={reference.generatedAssetId}
            className="overflow-hidden rounded-2xl border bg-card shadow-xs"
          >
            <a
              href={reference.signedUrl}
              target="_blank"
              rel="noreferrer"
              className="block aspect-square bg-muted/20 transition-opacity hover:opacity-90"
            >
              <img
                src={reference.signedUrl}
                alt={`${roleLabel} for ${reference.carouselFormatKey}`}
                className="size-full object-contain"
              />
            </a>

            <div className="space-y-3 p-3 text-xs text-muted-foreground">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-foreground">{roleLabel}</div>
                  <CodeText className="block break-all">{reference.blockId}</CodeText>
                </div>
                <CodeText className="shrink-0 rounded-full bg-muted px-2 py-0.5">
                  {reference.generatedAssetId.slice(0, 8)}
                </CodeText>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <ReferenceBadge>{modeLabel}</ReferenceBadge>
                <ReferenceBadge>{reference.carouselFormatKey}</ReferenceBadge>
                <ReferenceBadge>{`${reference.width ?? "?"} x ${reference.height ?? "?"}`}</ReferenceBadge>
              </div>

              <div className="space-y-1 border-t pt-3">
                <SectionLabel className="text-[10px] tracking-wide">Prompt</SectionLabel>
                <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/30 p-2 whitespace-pre-wrap break-words text-[11px] leading-5 text-foreground">
                  {reference.prompt}
                </div>
              </div>

              <div className="grid gap-3 border-t pt-3 md:grid-cols-2">
                <div className="space-y-1">
                  <SectionLabel className="text-[10px] tracking-wide">Cast Labels</SectionLabel>
                  {reference.recurringCastLabels.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {reference.recurringCastLabels.map((label) => (
                        <ReferenceBadge key={label}>{label}</ReferenceBadge>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed px-2 py-2 text-[11px]">
                      No cast labels recorded.
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <SectionLabel className="text-[10px] tracking-wide">Setting/Object</SectionLabel>
                  <div className="rounded-md border bg-muted/20 px-2 py-2 text-[11px] leading-5 text-foreground">
                    {reference.recurringSettingOrObject ?? "No setting/object recorded."}
                  </div>
                </div>
              </div>

              <div className="space-y-1 border-t pt-3">
                <SectionLabel className="text-[10px] tracking-wide">Storage Trace</SectionLabel>
                <div className="space-y-1 rounded-md border bg-muted/20 p-2">
                  <CodeText className="block break-all">{reference.objectKey}</CodeText>
                  <CodeText className="block break-all">task role: {reference.taskRole}</CodeText>
                  <CodeText className="block break-all">model: {reference.model}</CodeText>
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
